import { useHistory } from 'react-router-dom';

import { LmBadgeStatus, LmText, LmTooltip, useOnMouseEnter } from '@mes-ui/lemma';
import { Box, Stack, useMediaQuery } from '@mui/material';
import { WidgetsData } from 'api/types/widgets';
import { GiaType } from 'const';
import { useAppSelector } from 'hooks';
import { emitYMEvent } from 'portfolio3/features/yandexMetrika';
import { commonTheme } from 'portfolio3/styles/theme';
import { userVisibilitySettingsSelector } from 'selectors';
import { findTargetVisibilitySetting } from 'utils/localVisibilitySettingsService';

import CommonContent from '../CommonContent';
import { WIDGET_BLOCKS } from '../consts';
import OlympiadContent from '../OlympiadContent';
import PointsContent from '../PointsContent';
import { getWidgetVisibilitySettingsTypeCodes } from '../utils';

import './index.scss';

export type WidgetCardProps = {
  widgetData: WidgetsData[number];
};

const otherGiaTypes = [GiaType.writing, GiaType.narration, GiaType.interviewRus];

const WidgetCard = ({ widgetData }: WidgetCardProps) => {
  const history = useHistory();
  const visibilitySettings = useAppSelector(userVisibilitySettingsSelector);
  const visibilitySettingsTypeCodes = getWidgetVisibilitySettingsTypeCodes(widgetData.blockName);
  const widgetVisibilitySetting = findTargetVisibilitySetting(visibilitySettings, visibilitySettingsTypeCodes);
  const isVisible = widgetVisibilitySetting?.isVisible ?? false;

  const { isMouseEnter, handleMouseLeave, handleMouseEnter } = useOnMouseEnter();
  const isMobile = useMediaQuery(commonTheme.breakpoints.down('commonSm'));
  const sectionName = WIDGET_BLOCKS[widgetData.blockName].sectionName;

  const renderContent = () => {
    switch (widgetData.blockName) {
      case 'OLYMPIAD':
        return <OlympiadContent data={widgetData.data} />;
      case 'INDEPENDENT_DIAGNOSIC':
      case 'INDIVIDUAL_DIAGNOSIC':
      case 'GIA_EXAM':
        return <PointsContent widgetData={widgetData} />;
      default:
        return <CommonContent widgetData={widgetData} />;
    }
  };

  const renderBlockName = () => {
    switch (widgetData.blockName) {
      case 'GIA_EXAM':
        if (otherGiaTypes.includes(widgetData.data.examFormCode)) {
          return 'Другое';
        }

        if (
          widgetData.data.approbation &&
          (widgetData.data.examFormCode === GiaType.ege || widgetData.data.examFormCode === GiaType.oge)
        ) {
          return `Пробный ${widgetData.data.examFormCode}`;
        }

        return widgetData.data.examFormCode;
      default:
        return WIDGET_BLOCKS[widgetData.blockName].blockName;
    }
  };

  const handleClick = () => {
    let url = WIDGET_BLOCKS[widgetData.blockName].link;

    if (widgetData.blockName === 'GIA_EXAM') {
      const examBlockHash =
        widgetData.data.approbation && widgetData.data.examFormCode === GiaType.ege ? '#trialExam' : '#gia';
      url = `${url}${examBlockHash}`;
    }

    if (WIDGET_BLOCKS[widgetData.blockName].searchParam) {
      const [link, hash] = url.split('#');
      url = `${link}?${WIDGET_BLOCKS[widgetData.blockName].searchParam}=${widgetData.data.id}#${hash}`;
    }

    let subject = '';

    switch (widgetData.blockName) {
      case 'OLYMPIAD':
        subject = widgetData.data.subjectName || widgetData.data.name;
        break;
      case 'GIA_EXAM':
        subject = widgetData.data.subjectName;
        break;
      case 'INDEPENDENT_DIAGNOSIC':
      case 'INDIVIDUAL_DIAGNOSIC':
        subject = widgetData.data.selfDiagnosticSubjectName;
        break;
      default:
        subject = '';
    }

    emitYMEvent({
      type: 'portfolio_widgets_card_open_click',
      payload: {
        section: sectionName,
        title: renderBlockName(),
        subtitle: widgetData.blockName === 'OLYMPIAD' ? widgetData.data.reward ?? 'Участник' : '',
        subject,
        urlContent: url,
      },
    });
    history.push(url);
  };

  return (
    <LmTooltip
      open={!isVisible && isMouseEnter}
      placement="bottom"
      content="Этот раздел скрыт. Для просмотра необходимо изменить настройки отображения"
      withArrow
    >
      <Box
        className="widget-card"
        onClick={handleClick}
        onMouseEnter={!isVisible ? handleMouseEnter : undefined}
        onMouseLeave={!isVisible ? handleMouseLeave : undefined}
      >
        <Stack spacing={isMobile ? 1.5 : 2}>
          <Stack
            direction="row"
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <LmText ellipsis variant={isMobile ? 'lg' : 'h6'} weight="semi-bold">
              {renderBlockName()}
            </LmText>
            <LmBadgeStatus style={{ marginLeft: '16px', whiteSpace: 'nowrap' }}>{sectionName}</LmBadgeStatus>
          </Stack>
          {renderContent()}
        </Stack>
      </Box>
    </LmTooltip>
  );
};

export default WidgetCard;
